import { deleteQueryCache, updateQueryCache } from '@rhizome/codegen';
import {
  IAllTagsQuery,
  IChangeNoteTagsQuery,
  IDeleteNoteQuery,
  INoteByIdResult,
  INotesByTagsResult,
  ISetPublishNoteQuery,
  IUpdateTitleQuery,
} from '@rhizome/pgtypes';
import { ExpandLess, ExpandMore } from '@styled-icons/material';
import Tippy from '@tippyjs/react';
import TippyHeadless from '@tippyjs/react/headless';
import { queryClient } from 'apps/exegesis/pages/_app';
import { useCombobox, UseComboboxStateChange } from 'downshift';
import { deleteFolderContents } from 'libs/shared-firebase/src';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { colors, DefaultFont } from '../../styles/themes';
import { useFetcher, useMutationHook } from '../../utils/DataFetchers';
import { logAppEvent } from '../../utils/useAnalytics';
import { CreateMentionSlateComponent } from './plugins/TagMentionSlateComponent';
import { NoteIdContext } from './SlateEditorWrapper';

const MentionComponent = CreateMentionSlateComponent(true);

function NoteControls(props: {
  title: string | undefined;
  published: boolean;
  noteTags: string[];

  author: string;
}) {
  const noteId = useContext(NoteIdContext);

  const { mutateAsync: UpdateTitle } = useMutationHook<IUpdateTitleQuery>({
    route: 'UpdateTitle',
  });

  const { mutateAsync: DeleteNote } = useMutationHook<IDeleteNoteQuery>({
    route: 'DeleteNote',
  });

  const { mutateAsync: ChangeNoteTags } = useMutationHook<IChangeNoteTagsQuery>(
    {
      route: 'ChangeNoteTags',
    }
  );

  const { mutateAsync: SetPublishNote } = useMutationHook<ISetPublishNoteQuery>(
    {
      route: 'SetPublishNote',
    }
  );

  const settingsPublish = async () => {
    await SetPublishNote({
      publish: true,
      note_id: noteId,
    });
    queryClient.invalidateQueries(['NoteById', { note_id: noteId }]);
    updateQueryCache<INoteByIdResult>(
      queryClient,
      ['NoteById', { note_id: noteId }],
      {
        note_public: true,
      }
    );
  };

  const settingsUnpublish = async () => {
    await SetPublishNote({
      publish: false,
      note_id: noteId,
    });
    queryClient.invalidateQueries(['NoteById', { note_id: noteId }]);
    updateQueryCache<INoteByIdResult>(
      queryClient,
      ['NoteById', { note_id: noteId }],
      {
        note_public: false,
      }
    );
  };

  const [tagInput, setTagInput] = useState('');

  const router = useRouter();

  const { data: autocompleteTags } = useFetcher<IAllTagsQuery>(
    [
      'AllTags',
      {
        tags: [],
        public: false,
        author: null,
      },
    ],
    {}
  );

  const autocompleteTagStrings =
    (autocompleteTags?.map((tag) => tag.tags) as string[]) || [];

  const promptForNewTitle = () => {
    let newTitle: string | null = window.prompt(
      `Enter a new title.\n\n
        Note: links to this page will be renamed as well`,
      props.title || ''
    );
    // if user clicks cancel
    if (newTitle == null) return;

    newTitle = newTitle.trim();
    if (newTitle?.length === 0) {
      newTitle = null;
    }

    UpdateTitle({
      title: newTitle,
      note_id: noteId,
    }).catch((err) => {
      window.alert('A note with that title already exists.');
    });
    updateQueryCache<INoteByIdResult>(
      queryClient,
      ['NoteById', { note_id: noteId }],
      {
        note_title: newTitle,
      }
    );
  };

  const noteTagButtons = props.noteTags?.map((tag) => {
    return (
      <span
        key={tag}
        style={{
          marginRight: '5px',
          marginBottom: '5px',
          display: 'inline-block',
        }}
      >
        {/* <Tag>
          <Link href={'/notes/tag/' + tag}>{tag}</Link>
        </Tag>
        <TagDeleteButton
          onClick={() => {
            const newTags = [...props.noteTags];
            newTags.splice(newTags.indexOf(tag), 1);
            ChangeNoteTags({
              tags: newTags,
              note_id: noteId,
            });
            updateQueryCache<INoteByIdResult>(
              queryClient,
              ['NoteById', { note_id: noteId }],
              {
                note_tags: newTags,
              }
            );
          }}
        >
          X
        </TagDeleteButton> */}
        <MentionComponent attributes={{}} element={{ value: tag }} />
      </span>
    );
  });

  const TagInputSubmission = (e: Event) => {
    e.preventDefault();
    setTagInput('');
    if (!props.noteTags.includes(tagInput)) {
      const newTags = [...props.noteTags, tagInput];
      ChangeNoteTags({
        tags: newTags,
        note_id: noteId,
      });
      updateQueryCache<INoteByIdResult>(
        queryClient,
        ['NoteById', { note_id: noteId }],
        {
          note_tags: newTags,
        }
      );
    }
  };

  const [showSettings, setShowSettings] = useState(false);
  const toggleSettings = () => {
    setShowSettings(!showSettings);
  };
  const closeSettings = () => {
    setShowSettings(false);
  };

  const deleteNoteButtonHandler = async () => {
    if (
      window.confirm(
        'Are you sure you want to delete this note? This cannot be undone.'
      )
    ) {
      logAppEvent('note_deleted');
      deleteFolderContents(`images/${props.author}/${noteId}`);
      await DeleteNote({
        note_id: noteId,
      });
      deleteQueryCache<INotesByTagsResult>(queryClient, ['NotesByTags'], {
        note_id: noteId,
      });
      router.push('/notes/editor/');
    }
  };

  const [inputItems, setInputItems] = useState(autocompleteTagStrings);
  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    highlightedIndex,
    getItemProps,
  } = useCombobox({
    items: inputItems,
    onInputValueChange: (changes: UseComboboxStateChange<string>) => {
      const { inputValue, type } = changes;
      if (type !== useCombobox.stateChangeTypes.InputChange || !inputValue) {
        return;
      }
    },
    onSelectedItemChange: (changes: UseComboboxStateChange<string>) => {
      console.log('working!');
      const selectedTagInput = changes?.selectedItem?.trim() || '';
      setTagInput('');
      setInputItems(autocompleteTagStrings);
      if (!props.noteTags.includes(selectedTagInput)) {
        const newTags = [...props.noteTags, selectedTagInput];
        ChangeNoteTags({
          tags: newTags,
          note_id: noteId,
        });
        updateQueryCache<INoteByIdResult>(
          queryClient,
          ['NoteById', { note_id: noteId }],
          {
            note_tags: newTags,
          }
        );
      }
    },
  });
  // delete tags on backspace
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      console.log(document.activeElement);
      if (
        e.key === 'Backspace' &&
        document?.activeElement?.className.includes('page-tag-input') &&
        tagInput.length == 0
      ) {
        const newTags = [...props.noteTags];
        newTags.splice(newTags.length - 1, 1);
        ChangeNoteTags({
          tags: newTags,
          note_id: noteId,
        });
        updateQueryCache<INoteByIdResult>(
          queryClient,
          ['NoteById', { note_id: noteId }],
          {
            note_tags: newTags,
          }
        );
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [ChangeNoteTags, noteId, props.noteTags, tagInput.length]);

  return (
    <ControlWrapper>
      <div>
        <TippyHeadless
          interactive={true}
          offset={[0, 0]}
          placement="bottom-start"
          visible={showSettings}
          arrow={false}
          onClickOutside={closeSettings}
          render={(attrs) => (
            <CustomTippy className="box" tabIndex={-1} {...attrs}>
              <SettingsButton
                onClick={deleteNoteButtonHandler}
                style={{ color: 'red' }}
              >
                delete note
              </SettingsButton>
              {!props.published ? (
                <SettingsButton onClick={settingsPublish}>
                  publish note
                </SettingsButton>
              ) : null}
              {props.published ? (
                <SettingsButton onClick={settingsUnpublish}>
                  unpublish note
                </SettingsButton>
              ) : null}
            </CustomTippy>
          )}
        >
          <TippyButton onClick={toggleSettings}>
            {showSettings ? (
              <ExpandLess
                width="30px"
                style={{ verticalAlign: 'text-bottom', zIndex: 99 }}
              />
            ) : (
              <ExpandMore
                width="30px"
                style={{ verticalAlign: 'text-bottom', zIndex: 99 }}
              />
            )}
          </TippyButton>
        </TippyHeadless>
        <NoteTitle onClick={promptForNewTitle}>
          {props.title || 'untitled note'}
        </NoteTitle>
        {props.published ? (
          <Tippy
            interactive={true}
            placement="bottom"
            content={
              <span>
                Your note is live at <Link href={'/post/' + noteId}>here</Link>
                {'. All changes are synced in real time.'}
              </span>
            }
          >
            <Sphere></Sphere>
          </Tippy>
        ) : null}
      </div>

      <TagsAndTagInputRow>
        {noteTagButtons}
        <TagInputForm onSubmit={TagInputSubmission} {...getComboboxProps()}>
          <TagInput
            {...getInputProps()}
            onClick={getToggleButtonProps().onClick}
            value={tagInput}
            placeholder={'add a tag to this page'}
            className="page-tag-input"
            onChange={(e) => {
              console.log(e);
              setTagInput(e.target.value);
              setInputItems(
                autocompleteTagStrings.filter((item) =>
                  item?.toLowerCase().startsWith(e.target.value.toLowerCase())
                )
              );
            }}
          />
        </TagInputForm>
        <DropshiftList {...getMenuProps()} className="popper-click">
          {isOpen &&
            inputItems.map((item, index) => (
              <DropshiftItem
                className="popper-click"
                selected={highlightedIndex === index}
                key={`${item}${index}`}
                {...getItemProps({ item, index })}
              >
                {item}
              </DropshiftItem>
            ))}
        </DropshiftList>
      </TagsAndTagInputRow>
    </ControlWrapper>
  );
}

const DropshiftList = styled.ul`
  max-height: 180px;
  width: 300px;
  overflow-y: auto;
  margin: 0px;
  border-top: 0px;
  background-color: ${(props) => props.theme.colors.main.medium};
  position: absolute;
  z-index: 1000;
  list-style: none;
  padding: 0px;

  box-shadow: 1px 2px 14px -2px rgb(0 0 0 / 15%);
`;

const DropshiftItem = styled.li<{ selected: boolean }>`
  background-color: ${(props) =>
    props.selected ? props.theme.colors.main.dark : 'none'};
`;

const NoteTitle = styled.h1`
  display: inline-block;
  line-height: 1.3;
  margin: 0px;
`;

export const TagsAndTagInputRow = styled.div`
  font-size: 0.9rem;
  & * {
    line-height: normal;
  }

  /* override tag mention background color */
  & .tag-mention-node {
    /* background-color: red; */
    background-color: ${(props) => props.theme.colors.main.light};
    color: ${colors.lightOffBlack};
    :hover {
      outline: 1px solid ${colors.lightOffBlack};
    }
  }
`;

const ControlWrapper = styled.div`
  /* padding-left: 15px; */
  margin: 0px;
  padding-top: 55px;
  // to match slate editor offset
  padding-left: 0.5rem;
`;

const TagInputForm = styled.form`
  display: inline-block;
  padding: 0px;
  vertical-align: text-top;

  width: 150px;
  margin-bottom: 5px;
`;

const TagInput = styled.input`
  font-family: ${DefaultFont};
  border: none;
  font-size: 1.05em;
  border-radius: 0.2em;
  padding: 0.1em;
  /* margin: 2.5px 5px 5px 0px; */
  background-color: ${(props) => props.theme.colors.main.medium};
`;

const SettingsButton = styled.button`
  display: block;
  background-color: transparent;
  border: none;
  font-size: 1rem;
  padding-top: 2px;
  padding-bottom: 2px;
  width: 100%;
  text-align: left;
  :hover {
    background-color: ${(props) => props.theme.colors.main.light};
  }
`;

const TippyButton = styled.button`
  display: inline-block;
  background-color: transparent;
  border: none;
  width: 30px;
  margin-right: 10px;
  margin-top: 0px;
  margin-bottom: 0px;
  margin-left: -40px;
  padding: 0px;
`;

const CustomTippy = styled.div`
  border-radius: 0.5rem;
  background-color: ${(props) => props.theme.backgroundColor || 'white'};
  border: 1px solid black;
  padding: 10px 0px 10px 0px;
  width: 150px;
  -webkit-box-shadow: 1px 2px 14px -2px rgba(0, 0, 0, 0.15);
  box-shadow: 1px 2px 14px -2px rgba(0, 0, 0, 0.15);
`;

const Sphere = styled.span`
  height: 10px;
  width: 10px;
  background-color: ${(props) => props.theme.colors.highlight};
  border-radius: 50%;
  display: inline-block;
  margin-left: 10px;
  vertical-align: super;
`;

export default NoteControls;
